import { Injectable } from '@angular/core';

/**
 * PhoneNumberService - Phone number formatting and validation
 *
 * Responsibilities:
 * - Format phone numbers to E.164 format for Telnyx
 * - Validate phone numbers
 * - Support multiple country formats (US, Albania, international)
 */
@Injectable({
  providedIn: 'root'
})
export class PhoneNumberService {

  /**
   * Format phone number to E.164 format for Telnyx
   * Returns null if the number is invalid
   */
  formatToE164(phoneNumber: string): string | null {
    if (!phoneNumber) {
      return null;
    }

    // Already in E.164 format
    if (phoneNumber.startsWith('+')) {
      return this.isValidE164(phoneNumber) ? phoneNumber : null;
    }

    // Remove all non-digit characters
    const digits = phoneNumber.replace(/\D/g, '');

    if (digits.length < 8) {
      return null;
    }

    // Albanian mobile numbers (67X, 68X, 69X - 9 digits)
    if (digits.length === 9 && /^6[789]/.test(digits)) {
      return `+355${digits}`;
    }

    // Albanian with country code (355 + 9 digits = 12 total)
    if (digits.length === 12 && digits.startsWith('355')) {
      return `+${digits}`;
    }

    // US/Canada with country code (1 + 10 digits = 11 total)
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    }

    // US/Canada 10-digit (add +1)
    if (digits.length === 10 && !digits.startsWith('0')) {
      return `+1${digits}`;
    }

    // International format (10+ digits, assume has country code)
    if (digits.length >= 10) {
      return `+${digits}`;
    }

    return null;
  }

  /**
   * Validate E.164 format
   */
  isValidE164(phoneNumber: string): boolean {
    // E.164: + followed by 8-15 digits
    return /^\+[1-9]\d{7,14}$/.test(phoneNumber);
  }

  /**
   * Extract digits only from phone number
   */
  extractDigits(phoneNumber: string): string {
    return phoneNumber.replace(/\D/g, '');
  }

  /**
   * Format for display (e.g., +1 (555) 123-4567)
   */
  formatForDisplay(phoneNumber: string): string {
    const digits = this.extractDigits(phoneNumber);

    // US format
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }

    // Albanian format
    if (digits.length === 12 && digits.startsWith('355')) {
      return `+355 ${digits.slice(3, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
    }

    // Default: just add + prefix if needed
    return phoneNumber.startsWith('+') ? phoneNumber : `+${digits}`;
  }

  /**
   * Check if phone number looks valid (basic validation)
   */
  isValidPhoneNumber(phoneNumber: string): boolean {
    const formatted = this.formatToE164(phoneNumber);
    return formatted !== null;
  }
}
